import React, { useState } from 'react';
import { 
  Box, 
  Chip, 
  Typography, 
  FormControlLabel, 
  Switch,
} from '@mui/material';
import { palette, spaces } from '../../../assets/styles/themes/tokens';
import useScreenSize from '../../../utils/useScreenSize';
import { useTranslation } from 'react-i18next';
import { DiscountInformation, LicenceModel } from '@klab-berlin/api-sdk/lib/types/responses/Billing';
import { currencyFormat } from '../../../utils/format';
import { PaymentPeriod } from '@klab-berlin/api-sdk/lib/types/common';
import { useSelector } from 'react-redux';
import { getSubscriptionUpgradePreview, selectUser } from '../../../reducers/user.selectors';
import { getSubscription } from '../../../reducers/user.selectors';
import SubscriptionPaymentDetails from '../../../components/SubscriptionPaymentDetails';
interface LicenceInfoProps {
    licence: LicenceModel;
    library?: string;
    toggleSelectedLicenceModel: () => void;
    promoCode: string;
    promoCodeDiscountInfo?: DiscountInformation;
    setPromoCode: (promoCode: string) => void;
    setPromoCodeDiscountInfo: (discountInfo?: DiscountInformation) => void;
    setSelectedPaymentRhythm: (paymentPeriod: PaymentPeriod) => void;
    selectedPaymentRhythm: PaymentPeriod;
    paymentRhythms: PaymentPeriod[];
}

const LicenceInfo: React.FC<LicenceInfoProps> = (props) => {
  const { t } = useTranslation();
  const { 
    licence,
    library,
    toggleSelectedLicenceModel,
    promoCode,
    setSelectedPaymentRhythm,
    selectedPaymentRhythm,
    paymentRhythms,
    setPromoCodeDiscountInfo: setDiscountInfo,
    setPromoCode,
  } = props;

  const { 
    name, 
    landingPageTitle,
    displayTextLines, 
    isTrial,
    isRef,
    price,
    period,
    totalPrice
  } = licence;

  const { isLargeScreen } = useScreenSize();
  const [isTraineeDiscountEnabled, setIsTraineeDiscountEnabled] = useState<boolean>(isRef || false);
  const [newTotalPrice, setNewTotalPrice] = useState<number|undefined>(undefined);
  const [promoCodeDiscountInfo, setPromoCodeDiscountInfo] = useState<DiscountInformation>();
  const subscriptionUpgradePreview = useSelector(getSubscriptionUpgradePreview);
  const currentSubscription = useSelector(getSubscription);
  const user = useSelector(selectUser);

  const hasSubscriptionPreviewData = !!((user?.isDocumentUser || user?.isCourseUser) && subscriptionUpgradePreview);
  const hidePromoCode = (user?.isDocumentUser || user?.isCourseUser) || (isTrial && !promoCode);
  
  const hasNewPrice = newTotalPrice !== undefined;
  const previousPrice = hasNewPrice ? totalPrice : undefined;
  const currentPrice = hasNewPrice ? newTotalPrice : totalPrice;
  
  const handlePromoCodeValidated = (discountInformation: DiscountInformation, promoCode: string) => {
    setPromoCodeDiscountInfo(discountInformation);
    setDiscountInfo(discountInformation);
    setNewTotalPrice(discountInformation.newTotalPrice);
    setPromoCode(promoCode);
  };
  
  const handlePromoCodeActiveLayoutInit = () => {
    setPromoCodeDiscountInfo(undefined);
    setDiscountInfo(undefined);
    setNewTotalPrice(undefined);
  };

  const getPricePerMonth = (price: number) => promoCodeDiscountInfo?.newPricePerMonth
    ? currencyFormat(promoCodeDiscountInfo?.newPricePerMonth)
    : currencyFormat(price);

  const handleTraineeDiscountToggle = () => {
    toggleSelectedLicenceModel();
    setIsTraineeDiscountEnabled(prevState => !prevState);
  };

  const getAmountDueTodayFromLicence = () => {
    const priceForPaymentRythms: {[key in PaymentPeriod]: string} = {
      monthly: currencyFormat(price),
      quarterly: currencyFormat(price * 3),
      annually: currencyFormat(price * 12),
      fullAmount: currencyFormat(totalPrice)
    };
  
    return priceForPaymentRythms[selectedPaymentRhythm];
  };
  
  const getAmountDueTodayFromDiscountInfo = (promoCodeDiscountInfo: DiscountInformation) => {
    return currencyFormat(promoCodeDiscountInfo.firstPayment[selectedPaymentRhythm]);
  };
  
  let amountDueToday = currencyFormat(0);
  if (!isTrial) {
    amountDueToday = promoCodeDiscountInfo && promoCodeDiscountInfo.valid
      ? getAmountDueTodayFromDiscountInfo(promoCodeDiscountInfo)
      : getAmountDueTodayFromLicence();
  }

  const renderLicenceDescription = () => {
    let descriptionText = displayTextLines && displayTextLines.join(' ');

    if (!library && subscriptionUpgradePreview?.allowToUpgrade) {
      if (user?.isDocumentUser) {
        descriptionText = subscriptionUpgradePreview.payPeriod === 12 ? t(
          'When switching to meinUnterricht 360, the price of your subscription will increase by <totalAmount>. ' +
          'The existing contract duration until <oldSubscriptionEndDate> remains unchanged.',
          {
            totalAmount: currencyFormat(subscriptionUpgradePreview.totalAmount),
            oldSubscriptionEndDate: subscriptionUpgradePreview.oldSubscription.endDate,
          }
        ) : t(
          'When switching to meinUnterricht 360, the price of your subscription will increase to ' +
          '<totalAmount> until the end of your contract term. ' +
          'The existing contract duration until <oldSubscriptionEndDate> remains unchanged.',
          {
            totalAmount: currencyFormat(subscriptionUpgradePreview.totalAmount),
            oldSubscriptionEndDate: subscriptionUpgradePreview.oldSubscription.endDate,
          }
        );
      } else if (user?.isCourseUser) {
        descriptionText = t(
          'You will be upgraded from your current Schulflix meineFortbildung subscription ' +
          '(<oldPricePerMonth>) to meinUnterricht 360 (<newPricePerMonth>). Your subscription period ' +
          'will remain unchanged until <oldSubscriptionEndDate>.',
          {
            oldPricePerMonth: currencyFormat(Number(subscriptionUpgradePreview.oldLicence.pricePerMonth)),
            newPricePerMonth: currencyFormat(Number(subscriptionUpgradePreview.newLicence.pricePerMonth)),
            oldSubscriptionEndDate: subscriptionUpgradePreview.oldSubscription.endDate,
          }
        );
      }
    }
      
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            flexDirection: isLargeScreen ? 'row' : 'column',
            gap: spaces.xs,
          }}
        >
          <Typography 
            variant='h4'
            color={palette.text.primary}
          >
            {landingPageTitle || name}
          </Typography>
          {(isTrial || isRef) && (
            <Chip
              data-testid='premium-chip'
              label={t(isRef ? 'For trainees' : 'meinUnterrich Pro') }    
              variant='outlined' 
              color='primary'
              size='small'
            />
          )}  
        </Box>
        {descriptionText && (
          <Typography variant='body2' data-testid='licence-description-text'>
            {descriptionText}
          </Typography>
        )}
        {isTrial && !promoCode && (
          <FormControlLabel 
            control={
              <Switch
                data-testid='trainee-discount-toggle' 
                checked={isTraineeDiscountEnabled}
                onChange={handleTraineeDiscountToggle}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            }
            label={t('Trainee Discount')}
            labelPlacement='start'
            sx={{
              width: 'fit-content',
              '&.MuiFormControlLabel-root': {
                gap: spaces.xs,
                justifyContent: 'flex-end',
                marginLeft: 0,
                marginBottom: 0,
              },
            }}
          />  
        )}
      </>
    );
  };

  const renderLicencePrice = () => {
    return (
      <>
        <Typography
          variant='body2'
          color={palette.text.primary}
        >
          {isTrial ? t('Have a test') : t('Your chosen package')}
        </Typography>
        <Box>
          <Typography
            variant='h2'
            color={palette.text.primary}
            data-testid='licence-info-area__monthly-price'
          >
            {getPricePerMonth(price)} mtl.
          </Typography>
          {!library && (
            <Typography
              variant='caption'
              color={palette.text.primary}
            >
              {t('<termLength> months duration', { termLength: period })}
            </Typography>
          )}   
        </Box>  
      </>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spaces.lg,
      }}
    >
      <Box
        data-testid='licence-description'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.sm,
        }}
      >
        {renderLicenceDescription()}
      </Box>
      {!user?.isDocumentUser && !user?.isCourseUser && (
        <Box
          data-testid='licence-price'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spaces.sm,
          }}
        >
          {renderLicencePrice()}
        </Box>
      )}
      {!library && (
        <Box data-testid='surface-payment-table'>
          <SubscriptionPaymentDetails
            licence={licence}
            hasSubscriptionPreviewData={hasSubscriptionPreviewData}
            previousPrice={previousPrice}
            currentPrice={currentPrice}
            amountDueToday={amountDueToday}
            promoCode={promoCode}
            hidePromoCode={hidePromoCode}
            subscriptionUpgradePreview={subscriptionUpgradePreview}
            currentSubscription={currentSubscription}
            selectedPaymentRhythm={selectedPaymentRhythm}
            paymentRhythms={paymentRhythms}
            setSelectedPaymentRhythm={setSelectedPaymentRhythm}
            handlePromoCodeValidated={handlePromoCodeValidated}
            handlePromoCodeActiveLayoutInit={handlePromoCodeActiveLayoutInit}
            user={user}
          />
        </Box>
      )}
    </Box>
  );
};

export default LicenceInfo;
